import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Landing from "./components/landing";
import Language from "./components/language";
import GameIntro from "./components/gameIntro";
import CharacterSelection from "./components/characterSelection";
import PlayerDataForm from "./components/playerDataForm";
import AwarenessWelcome from "./components/awarenessWelcome";
import AwarenessInstructions from "./components/awarenessInstructions";
import AwarenessGame from "./components/awarenessGame";
import AwarenessWrapUp from "./components/awarenessWrapUp";
import AwarenessReflection from "./components/awarenessReflection";
import DilemmaInstructions from "./components/dilemmaInstructions";
import DilemmaRiskSelection from "./components/dilemmaRiskSelection";
import DilemmaGame from "./components/dilemmaGame";
import DilemmaReflection from "./components/dilemmaReflection";
import TransformationInstructions from "./components/transformationInstructions";
import TransformationGame from "./components/transformationGame";
import Congratulations from "./components/congratulations";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminDashboard from "./components/AdminDashboard";
import useGameState from "./hooks/useGameState";

function App() {
  const {
    selectedLanguage,
    setSelectedLanguage,
    selectedCharacters,
    setSelectedCharacters,
    gameProgress,
    updateProgress,
    resetGame,
    canAccessScreen
  } = useGameState();

  // Bloquear navegación del navegador (botones atrás/adelante)
  useEffect(() => {
    // Agregar una entrada al historial al iniciar
    window.history.pushState(null, null, window.location.pathname);
    
    // Función para manejar el evento popstate (cuando presionan atrás/adelante)
    const handlePopState = (event) => {
      // Prevenir la navegación volviendo a agregar la entrada actual
      window.history.pushState(null, null, window.location.pathname);
      
      // Mostrar mensaje opcional (puedes comentarlo si no lo quieres)
      console.log('⚠️ Navegación bloqueada. Usa los botones del juego para avanzar.');
    };

    // Escuchar el evento popstate
    window.addEventListener('popstate', handlePopState);

    // Limpiar el evento cuando se desmonte el componente
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return (
    <Router>
      <Routes>
        {/* Ruta principal - Landing */}
        <Route 
          path="/" 
          element={<Landing />} 
        />

        {/* Selección de idioma */}
        <Route 
          path="/language" 
          element={
            <Language 
              selectedLanguage={selectedLanguage}
              setSelectedLanguage={setSelectedLanguage}
              updateProgress={updateProgress}
            />
          } 
        />

        {/* Introducción del juego */}
        <Route 
          path="/intro" 
          element={
            <ProtectedRoute 
              canAccess={canAccessScreen('intro')}
              redirectTo="/language"
            >
              <GameIntro 
                updateProgress={updateProgress}
              />
            </ProtectedRoute>
          } 
        />

        {/* Selección de personajes */}
        <Route 
          path="/character-selection" 
          element={
            <ProtectedRoute 
              canAccess={canAccessScreen('character-selection')}
              redirectTo="/intro"
            >
              <CharacterSelection 
                selectedCharacters={selectedCharacters}
                setSelectedCharacters={setSelectedCharacters}
                updateProgress={updateProgress}
              />
            </ProtectedRoute>
          } 
        />

        {/* Formulario de datos de jugadores */}
        <Route 
          path="/player-data-form" 
          element={
            <ProtectedRoute 
              canAccess={canAccessScreen('player-data-form')}
              redirectTo="/character-selection"
            >
              <PlayerDataForm 
                selectedCharacters={selectedCharacters}
                updateProgress={updateProgress}
              />
            </ProtectedRoute>
          } 
        />

        {/* Bienvenida de Awareness */}
        <Route 
          path="/awareness" 
          element={
            <ProtectedRoute 
              canAccess={canAccessScreen('awareness')}
              redirectTo="/player-data-form"
            >
              <AwarenessWelcome 
                gameData={selectedCharacters}
                updateProgress={updateProgress}
              />
            </ProtectedRoute>
          } 
        />

        {/* Instrucciones de Awareness */}
        <Route 
          path="/awareness-instructions" 
          element={
            <ProtectedRoute 
              canAccess={canAccessScreen('awareness')}
              redirectTo="/awareness"
            >
              <AwarenessInstructions 
                gameData={selectedCharacters}
                updateProgress={updateProgress}
              />
            </ProtectedRoute>
          } 
        />

        {/* Juego: Awareness */}
        <Route 
          path="/game/awareness" 
          element={
            <ProtectedRoute 
              canAccess={canAccessScreen('game')}
              redirectTo="/awareness-instructions"
            >
              <AwarenessGame 
                updateProgress={updateProgress}
              />
            </ProtectedRoute>
          } 
        />

        {/* Wrap-Up de Awareness */}
        <Route 
          path="/awareness-wrapup" 
          element={
            <ProtectedRoute 
              canAccess={canAccessScreen('awareness-wrapup')}
              redirectTo="/game/awareness"
            >
              <AwarenessWrapUp 
                updateProgress={updateProgress}
              />
            </ProtectedRoute>
          } 
        />

        {/* Reflexión de Awareness */}
        <Route 
          path="/awareness-reflection" 
          element={
            <ProtectedRoute 
              canAccess={canAccessScreen('awareness-reflection')}
              redirectTo="/awareness-wrapup"
            >
              <AwarenessReflection 
                updateProgress={updateProgress}
              />
            </ProtectedRoute>
          } 
        />

        {/* Instrucciones de Dilema */}
        <Route 
          path="/dilema-instructions" 
          element={
            <DilemmaInstructions 
              gameData={selectedCharacters}
              updateProgress={updateProgress}
            />
          } 
        />

        {/* Selección de Riesgos para Dilema */}
        <Route 
          path="/dilema-risk-selection" 
          element={
            <ProtectedRoute 
              canAccess={canAccessScreen('dilema-risk-selection')}
              redirectTo="/dilema-instructions"
            >
              <DilemmaRiskSelection 
                updateProgress={updateProgress}
              />
            </ProtectedRoute>
          } 
        />

        {/* Juego: Dilema */}
        <Route 
          path="/game/dilema" 
          element={
            <DilemmaGame 
              updateProgress={updateProgress}
            />
          } 
        />

        {/* Reflexión de Dilema */}
        <Route 
          path="/dilemma-reflection" 
          element={
            <ProtectedRoute 
              canAccess={canAccessScreen('dilemma-reflection')}
              redirectTo="/game/dilema"
            >
              <DilemmaReflection 
                updateProgress={updateProgress}
              />
            </ProtectedRoute>
          } 
        />

        {/* Instrucciones de Transformation */}
        <Route 
          path="/transformation-instructions" 
          element={
            <TransformationInstructions 
              gameData={selectedCharacters}
              updateProgress={updateProgress}
            />
          } 
        />

        {/* Juego: Transformation */}
        <Route 
          path="/game/transformation" 
          element={
            <TransformationGame />
          } 
        />

        {/* Pantalla de Felicitaciones - Final del Juego */}
        <Route 
          path="/congratulations" 
          element={<Congratulations />} 
        />

        {/* Panel de administración (acceso con contraseña dentro del propio componente) */}
        <Route 
          path="/admin"
          element={<AdminDashboard />}
        />

        {/* Redirigir cualquier ruta no encontrada al inicio */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
